import { useEffect, useMemo, useRef, useState } from "react";
import { Bike, Car, Footprints, Star } from "lucide-react";
import ConciergeMap, { MapPoint } from "./map/ConciergeMap";
import { parseHotel } from "./PoiMapTemplate";
import { humanize } from "./map/categories";
import localization from "../../../../../assets/localization";

interface ItineraryTemplateProps {
  template: any;
  language: string;
}

type Stop = MapPoint & {
  walkMin: number | null;
  description: string;
  photoUrl: string | null;
  subtype: string | null;
};

const parseStops = (rawStops: any[]): Stop[] =>
  (rawStops ?? [])
    .map((stop: any, index: number) => {
      const lat = Number(stop?.lat);
      const lng = Number(stop?.long ?? stop?.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      const rating = Number(stop.rating);
      return {
        name: stop.name ?? "",
        lat,
        lng,
        category: stop.category ?? null,
        order: stop.order ?? index + 1,
        rating: isNaN(rating) || rating <= 0 ? null : Math.round(rating * 10) / 10,
        walkMin: stop.walk_min ?? stop.walkMin ?? null,
        description: stop.description ?? "",
        photoUrl: stop.photo_url ?? stop.photoUrl ?? null,
        subtype: stop.subtype ?? null,
      };
    })
    .filter(Boolean) as Stop[];

// A walking itinerary as a story: numbered stops on the map, connected by an
// animated route from the hotel, with a step rail the guest swipes through.
export default function ItineraryTemplate({
  template,
  language,
}: ItineraryTemplateProps) {
  const t = localization.get(language);
  const hotel = useMemo(() => parseHotel(template?.hotel), [template]);
  const stops = useMemo(() => parseStops(template?.stops), [template]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const railRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    railRef.current
      ?.querySelector(".cmap-card.active")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [selectedIndex]);

  if (!stops.length) return null;

  const totalWalk = stops.reduce(
    (sum, stop) => (stop.walkMin != null ? sum + stop.walkMin : sum),
    0
  );

  const transport = (["foot", "bike", "car"].includes(template?.mode)
    ? template.mode
    : "foot") as "foot" | "bike" | "car";
  const TransportIcon =
    transport === "bike" ? Bike : transport === "car" ? Car : Footprints;

  return (
    <div className="cmap-template">
      <ConciergeMap
        hotel={hotel}
        points={stops}
        mode="itinerary"
        transport={transport}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        language={language}
      />

      <div className="cmap-head">
        <span className="cmap-head-title">
          {template?.title || t.anchor_itinerary}
        </span>
        <span className="cmap-head-sub">
          {stops.length} {stops.length === 1 ? t.stop : t.stops}
          {totalWalk > 0 && (
            <>
              <span className="cmap-dot">·</span>
              <TransportIcon className="icon" /> {totalWalk}{" "}
              {transport === "foot" ? t.walk_min : "min"}
            </>
          )}
        </span>
      </div>

      <div className="cmap-rail" ref={railRef}>
        {hotel && (
          <div
            className="cmap-card start"
            onClick={() => setSelectedIndex(0)}
          >
            <div className="cmap-card-head">
              <span className="cmap-card-chip start">●</span>
              <span className="cmap-card-name">{hotel.name || t.your_hotel}</span>
            </div>
            <div className="cmap-card-meta">{t.itinerary_start}</div>
          </div>
        )}
        {stops.map((stop, index) => {
          const subtype = humanize(stop.subtype);
          return (
            <div
              key={index}
              className={`cmap-card ${
                index === selectedIndex ? "active" : ""
              }`}
              onClick={() => setSelectedIndex(index)}
            >
              <div className="cmap-card-head">
                <span className="cmap-card-chip number">{stop.order}</span>
                <span className="cmap-card-name">{stop.name}</span>
                {stop.photoUrl && (
                  <span className="cmap-stop-thumb">
                    <img
                      src={stop.photoUrl}
                      alt={stop.name}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.parentElement?.style.setProperty(
                          "display",
                          "none"
                        );
                      }}
                    />
                  </span>
                )}
              </div>
              <div className="cmap-card-meta">
                {stop.rating != null && (
                  <span className="cmap-card-rating inline">
                    <Star className="cmap-star" />
                    <span className="cmap-rating-num">{stop.rating}</span>
                  </span>
                )}
                {stop.rating != null && subtype && (
                  <span className="cmap-dot">·</span>
                )}
                {subtype && <span>{subtype}</span>}
                {(stop.rating != null || subtype) &&
                  stop.walkMin != null && <span className="cmap-dot">·</span>}
                {stop.walkMin != null && (
                  <>
                    <Footprints className="icon" /> {stop.walkMin} {t.walk_min}
                  </>
                )}
              </div>
              {stop.description && (
                <div className="cmap-card-desc">{stop.description}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
